import PropTypes from 'prop-types';
import React from 'react';
import { Spinner } from '@unite-us/app-components';
import { usePopulate } from '../hooks/APIHooks';
import { useIsClientsDrawer } from '../context/InteractionsContext';
import { formatQuantityValue } from '../utils/formatQuantityValue';
import { InteractionCardField } from './InteractionCardField';

const formatDatePart = (isoString) => {
  if (!isoString) {
    return '';
  }

  const date = new Date(Date.parse(isoString));

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

export function CommunityEngagementInteractionCardBody({ interaction }) {
  const isClientsDrawer = useIsClientsDrawer();
  const { isLoading } = usePopulate('procedures', 'procedure', interaction, {
    queryConfig: { placeholderData: undefined },
  });
  const procedure = interaction.procedures?.[0] ?? null;

  if (isLoading) {
    return <Spinner scale={0.45} />;
  }

  const documentedBy = `${interaction.recorded_by.first_name} ${interaction.recorded_by.last_name}`;
  const orgName = interaction.performing_org.name;
  const note = interaction.note;
  const durationLabel = formatQuantityValue(procedure?.quantity_value);

  const performedStart = procedure?.performed_start_time ?? interaction.start_time;
  const performedEnd = procedure?.performed_end_time ?? interaction.end_time;
  const startDateLabel = formatDatePart(performedStart);
  const endDateLabel = formatDatePart(performedEnd);
  const dateRangeLabel = endDateLabel && endDateLabel !== startDateLabel ?
    `${startDateLabel} – ${endDateLabel}` :
    startDateLabel;
  const fieldGridClass = isClientsDrawer ? 'grid-cols-2' : 'grid-cols-4';

  return (
    <div className="space-y-4">
      <div className={`grid gap-x-4 gap-y-4 ${fieldGridClass}`}>
        <InteractionCardField icon="Group" iconAriaLabel="Interaction class">
          Community Engagement
        </InteractionCardField>
        <InteractionCardField icon="User" iconAriaLabel="Documented by">
          {documentedBy}
        </InteractionCardField>
        <InteractionCardField icon="Calendar" iconAriaLabel="Date">
          {dateRangeLabel}
        </InteractionCardField>
        {durationLabel && (
          <InteractionCardField icon="Clock" iconAriaLabel="Duration">
            {durationLabel}
          </InteractionCardField>
        )}
      </div>

      <InteractionCardField icon="Building" iconAriaLabel="Organization">
        {orgName}
      </InteractionCardField>

      {note && (
        <InteractionCardField icon="FileTextO" iconAriaLabel="Note">
          {note}
        </InteractionCardField>
      )}
    </div>
  );
}

CommunityEngagementInteractionCardBody.propTypes = {
  interaction: PropTypes.object.isRequired,
};
